import React, { useContext, useEffect, useRef, useState } from 'react';
import { UploadContext } from '../DataContexts/UploadContext';
import './PlaylistBackgroundEditor.css';

const S3_BASE = 'https://tdj-s3.s3.eu-west-2.amazonaws.com/';
/** Folder the player downloads playlist backgrounds from (PlaylistBackgroundsDirectory). */
export const PLAYLIST_BACKGROUNDS_URL = `${S3_BASE}/playlistbackgrounds/`;
export const playlistBackgroundUrl = (fileName) => (fileName ? `${PLAYLIST_BACKGROUNDS_URL}${encodeURIComponent(fileName)}` : null);

/**
 * Playlist background control of the Playlist Editor header (DJB-235).
 * A playlist has at most one background (PLAYLIST.backgroundImage, bare file
 * name in playlistbackgrounds/); it applies on every machine that opens the
 * playlist. Add/Replace uploads a file through
 * POST /api/storage/uploadPlaylistBackgroundImage and hands the stored file
 * name to `onSelect`; Remove calls `onRemove`.
 *
 * @param {Object} props
 * @param {string} props.playlistId
 * @param {string|null} [props.fileName]      current PLAYLIST.backgroundImage
 * @param {(fileName: string) => Promise<string|null|undefined>} props.onSelect
 *        persists the background; resolves with the file name the server kept
 *        (so a silently refused image can be reported), or undefined when the
 *        save failed (the caller already notified)
 * @param {() => Promise<void>} [props.onRemove]  clears the background; omit to hide Remove
 * @param {boolean} [props.disabled]
 */
const PlaylistBackgroundEditor = ({ playlistId, fileName = null, onSelect, onRemove, disabled = false }) => {
  const { uploadPlaylistBackgroundImage } = useContext(UploadContext);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [broken, setBroken] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setBroken(false);
    setNotice(null);
  }, [fileName, playlistId]);

  const src = broken ? null : playlistBackgroundUrl(fileName);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !onSelect) return;
    setBusy(true);
    setNotice(null);
    try {
      const stored = await uploadPlaylistBackgroundImage(file);
      if (!stored) return;
      const kept = await onSelect(stored);
      if (kept !== undefined && kept !== stored) {
        setNotice('The server kept the previous background; this image is not available as a playlist background.');
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;
    setBusy(true);
    setNotice(null);
    try {
      await onRemove();
    } finally {
      setBusy(false);
    }
  };

  const controlsDisabled = busy || disabled;
  const inputId = `pm-bg-file-${playlistId}`;

  return (
    <div className="pm-bg" data-testid="playlist-background">
      <div className="pm-bg-row">
        <div className="pm-bg-preview" aria-hidden={!src}>
          {src ? (
            <img src={src} alt="Playlist background" className="pm-bg-img" onError={() => setBroken(true)} />
          ) : (
            <span className="pm-bg-empty">{fileName ? 'Unavailable' : 'None'}</span>
          )}
        </div>
        <div className="pm-bg-text">
          <span className="pm-bg-label">Background</span>
          <span className={`pm-bg-name${fileName ? '' : ' pm-bg-name--empty'}`} title={fileName || undefined}>
            {fileName || 'No background'}
          </span>
        </div>
        <div className="pm-bg-actions">
          <label className={`pm-bg-btn pm-bg-btn--primary${controlsDisabled ? ' pm-bg-btn--disabled' : ''}`} htmlFor={inputId}>
            {busy ? 'Saving…' : fileName ? 'Replace' : 'Add background'}
          </label>
          <input id={inputId} ref={inputRef} type="file" accept="image/*" className="pm-bg-file" onChange={handleFileChange} disabled={controlsDisabled} />
          {fileName && onRemove && (
            <button type="button" className="pm-bg-btn pm-bg-btn--ghost" onClick={handleRemove} disabled={controlsDisabled}>
              Remove
            </button>
          )}
        </div>
      </div>
      {notice && <p className="pm-bg-notice" role="status">{notice}</p>}
    </div>
  );
};

export default PlaylistBackgroundEditor;
